import { format as dateFnsFormat, Locale } from 'date-fns';
import { enUS, es, fr, de, pt, it, zhCN, ja, ko, ar, ru, hi } from 'date-fns/locale';

const localeMap: Record<string, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  de: de,
  pt: pt,
  it: it,
  zh: zhCN,
  ja: ja,
  ko: ko,
  ar: ar,
  ru: ru,
  hi: hi,
};

/**
 * Format a date according to the user's language preference
 * @param date - The date to format
 * @param formatStr - The format string (date-fns format)
 * @param language - The language code (en, es, fr, de, pt)
 */
export function formatDate(date: Date | string | number, formatStr: string, language: string = 'en-US'): string {
  const locale = localeMap[language.toLowerCase()] || enUS;
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return dateFnsFormat(dateObj, formatStr, { locale });
}

/**
 * Format a number according to the user's language preference
 * @param num - The number to format
 * @param language - The language code (en, es, fr, de, pt)
 * @param options - Intl.NumberFormatOptions
 */
export function formatNumber(
  num: number,
  language: string = 'en-US',
  options?: Intl.NumberFormatOptions
): string {
  const localeCode = language.toLowerCase();
  return new Intl.NumberFormat(localeCode, options).format(num);
}

/**
 * Format a currency value according to the user's language preference
 * @param amount - The amount to format
 * @param currency - The currency code (USD, EUR, GBP, etc.)
 * @param language - The language code (en, es, fr, de, pt)
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  language: string = 'en-US'
): string {
  return formatNumber(amount, language, {
    style: 'currency',
    currency: currency,
  });
}

/**
 * Format a percentage value according to the user's language preference
 * @param value - The value to format (0-1 for 0-100%)
 * @param language - The language code (en, es, fr, de, pt)
 * @param decimals - Number of decimal places
 */
export function formatPercent(
  value: number,
  language: string = 'en-US',
  decimals: number = 0
): string {
  return formatNumber(value, language, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Get relative time string (e.g., "2 hours ago").
 *
 * Returns '' for null/undefined/invalid input rather than crashing. The
 * old behaviour was to pass through to Date and Intl.RelativeTimeFormat,
 * which throws "Invalid time value" (RangeError) for invalid dates and
 * "Cannot read properties of null" for nullish input — both of which
 * propagated up to the React error boundary and crashed the whole app.
 *
 * Callers that want a placeholder ('—', 'never') should pick one in
 * their JSX (e.g. `{x ? relativeTime(x) : '—'}`); the lib returning ''
 * preserves backward compatibility for callers that already do.
 *
 * @param date - The date to compare (or null/undefined for "no value")
 * @param language - The language code (en, es, fr, de, pt)
 */
export function formatRelativeTime(
  date: Date | string | number | null | undefined,
  language: string = 'en-US',
): string {
  if (date == null) return '';
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return '';
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(language.toLowerCase(), { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  } else if (diffInSeconds < 3600) {
    return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  } else if (diffInSeconds < 86400) {
    return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  } else if (diffInSeconds < 2592000) {
    return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  } else if (diffInSeconds < 31536000) {
    return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
  } else {
    return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
  }
}
